import type { EvaluateShippingRulesResponse } from '@/types';

/**
 * RS-22 — pure decision function for the add-new-order flow.
 *
 * Outcomes from the evaluate-shipping-rules response:
 *
 *   auto-dispatched   skip Step 2 — go directly to summary with the
 *                     transparency banner showing the assigned carrier
 *                     and winning rule.
 *   auto-cheapest     OBS-3 / NEW-1 — rule restricts the carrier set AND
 *                     mandates `cheapest` filter. After fetching pricing
 *                     for the restricted set, sort by shipping_fee asc
 *                     and skip Step 2 with index 0 pre-selected.
 *   restricted        Step 2 visible but only the rule's carriers. May
 *                     carry a recommended_filter (e.g. fastest) that
 *                     informs default sort order.
 *   manual            Step 2 visible with the full eligible list.
 *
 * Defensive: if auto_dispatched=true but no assigned_carrier_uid is
 * present, fall back to manual rather than crashing the order flow.
 */
export type RecommendedFilter = 'cheapest' | 'most_reliable' | 'fastest' | null;

export type OrderRoutingDecision =
  | {
      action: 'auto-dispatched';
      assignedCarrierUid: string;
      winningRuleUid: string | null;
      restrictedCarrierUids: [];
      recommendedFilter: RecommendedFilter;
    }
  | {
      action: 'auto-cheapest';
      assignedCarrierUid: null;
      winningRuleUid: string | null;
      restrictedCarrierUids: string[];
      recommendedFilter: 'cheapest';
    }
  | {
      action: 'restricted';
      assignedCarrierUid: null;
      winningRuleUid: string | null;
      restrictedCarrierUids: string[];
      recommendedFilter: RecommendedFilter;
    }
  | {
      action: 'manual';
      assignedCarrierUid: null;
      winningRuleUid: null;
      restrictedCarrierUids: [];
      recommendedFilter: null;
    };

export function decideOrderRouting(
  response: EvaluateShippingRulesResponse,
): OrderRoutingDecision {
  const o = response.object;
  const filter: RecommendedFilter = o.recommended_filter ?? null;

  if (o.auto_dispatched && o.assigned_carrier_uid) {
    return {
      action: 'auto-dispatched',
      assignedCarrierUid: o.assigned_carrier_uid,
      winningRuleUid: o.winning_rule_uid,
      restrictedCarrierUids: [],
      recommendedFilter: filter,
    };
  }

  if (o.restricted_carrier_uids && o.restricted_carrier_uids.length > 0) {
    if (filter === 'cheapest') {
      return {
        action: 'auto-cheapest',
        assignedCarrierUid: null,
        winningRuleUid: o.winning_rule_uid,
        restrictedCarrierUids: o.restricted_carrier_uids,
        recommendedFilter: 'cheapest',
      };
    }
    return {
      action: 'restricted',
      assignedCarrierUid: null,
      winningRuleUid: o.winning_rule_uid,
      restrictedCarrierUids: o.restricted_carrier_uids,
      recommendedFilter: filter,
    };
  }

  return {
    action: 'manual',
    assignedCarrierUid: null,
    winningRuleUid: null,
    restrictedCarrierUids: [],
    recommendedFilter: null,
  };
}
